/* eslint-disable indent */
import React, { useEffect, useState } from "react";
import {
  computeFirstPageNumberToDisplay,
  renderDisabledNextLink,
  renderDisabledPrevLink,
  renderEnabledNextLink,
  renderEnabledPrevLink,
} from "./pagination.helper";
import PageLink from "./common/PageLink";

/**
 * The default prop values were initialized in the parent so all props are required
 * in this component.
 */
export interface PaginationPagesProps extends React.HTMLProps<HTMLDivElement> {
  resultsCount: number;
  page: number;
  setPage: (page: number) => void;
  pageSize: number;
  numberOfPagesToDisplay: number;
  previousLabel: string;
  previousAriaLabel: string;
  nextLabel: string;
  nextAriaLabel: string;
  ariaItemLabel: string;
  pageClickHandler: (prevPage: number) => void;
}

/**
 * This component gets rendered all the times and cannot be turned of with a prop.
 *
 * The component uses mathematical calculations to compute number of pageItems to be
 * displayed and to enable/disable previous/next links
 */
export const PaginationPages = ({
  page,
  setPage,
  resultsCount,
  pageSize,
  numberOfPagesToDisplay,
  previousLabel,
  previousAriaLabel,
  nextLabel,
  nextAriaLabel,
  ariaItemLabel,
  pageClickHandler,
}: PaginationPagesProps): React.ReactElement => {
  /*
   * Used to compute total pageItems out of which 5 will be shown ideally.
   */
  const numberOfPages = Math.ceil(resultsCount / pageSize);

  /*
   * The number of items to be displayed between previous and next link is
   * configurable. By default its 5 items when sufficient data is present.
   */
  const numberOfPagesPerView =
    numberOfPages >= numberOfPagesToDisplay
      ? numberOfPagesToDisplay
      : numberOfPages;

  /*
   * computeFirstPageNumberToDisplay is a curry function which helps in calculating
   * the page number to be displayed in the UI based on pageNumber and numberOfPagesToDisplay
   */
  const computeIndexFn = computeFirstPageNumberToDisplay(numberOfPagesPerView);
  const [itemStartIndex, setItemStartIndex] = useState(computeIndexFn(page));

  /*
   * When pageSize is changed in the results per page dropdown, we need to rebuild the
   * page items between prev & next links.
   */
  useEffect(() => {
    setItemStartIndex(computeIndexFn(page));
  }, [pageSize, computeIndexFn, page]);

  /*
   * Conditional checks to enable or disable the next/prev
   */
  const disablePreviousBtn = page === 1;
  const disableNextBtn = page >= numberOfPages;

  /*
   * When next/prev button is clicked, we need to
   * 1. Change the page number (always)
   * 2. Change the startIndex (only if the pageNumber is not in the view)
   * @param type
   */
  const refreshPageNumberAndIndex = (type: "PREVIOUS" | "NEXT"): void => {
    const newPage = type === "PREVIOUS" ? page - 1 : page + 1;
    setPage(newPage);
    const newIndex = computeIndexFn(newPage);
    if (type === "NEXT") {
      if (newPage >= itemStartIndex + numberOfPagesToDisplay) {
        setItemStartIndex(newIndex);
      }
    } else {
      if (newPage < itemStartIndex) {
        setItemStartIndex(newIndex);
      }
    }

    pageClickHandler?.(newPage);
  };

  /*
   * Event handlers for prev and next buttons
   * preventDefault is required to prevent browser redirecting to href="#"
   */
  const prevBtnHandler = (
    e: React.MouseEvent<HTMLAnchorElement, MouseEvent>
  ): void => {
    refreshPageNumberAndIndex("PREVIOUS");
    e.preventDefault();
  };
  const nextBtnHandler = (
    e: React.MouseEvent<HTMLAnchorElement, MouseEvent>
  ): void => {
    refreshPageNumberAndIndex("NEXT");
    e.preventDefault();
  };

  /*
   * Callback that gets executed when pageItem is clicked,
   * preventDefault is required to prevent browser redirecting to href="#"
   */
  const _pageItemClickHandler = (
    e: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
    displayPageNumber: number
  ): void => {
    setPage(displayPageNumber);
    pageClickHandler?.(displayPageNumber);
    e.preventDefault();
  };

  /**
   * The numberOfPagesPerView is computed above and its used to generate anchor tags
   */
  return (
    <div className="spark-pagination__pages">
      {disablePreviousBtn
        ? renderDisabledPrevLink(previousAriaLabel, previousLabel)
        : renderEnabledPrevLink(
            previousAriaLabel,
            previousLabel,
            prevBtnHandler
          )}
      {Array(numberOfPagesPerView)
        .fill(0)
        .map((item, index) => {
          /*
           * Assume next button was clicked once, the itemStartIndex would be 6.
           * The number displayed upon looping would be 6,7,8,9,10
           */
          const displayPageNumber = index + itemStartIndex;

          /*
           * When we only have data for 2 pages, then we do not render the other 3 pages
           */
          if (displayPageNumber > numberOfPages) {
            return null;
          }

          /*
           * Removing href disables keyboard navigation. If developer wants to
           * do operations to URL on pageItem click, it need to be done in the
           * callback prop that gets executed on page item click.
           */
          return (
            <PageLink
              displayPageNumber={displayPageNumber}
              page={page}
              ariaItemLabel={ariaItemLabel}
              index={index}
              key={`index${displayPageNumber}${index}`}
              onClick={(e, numberDisplayed): void =>
                _pageItemClickHandler(e, numberDisplayed)
              }
            />
          );
        })}
      {disableNextBtn
        ? renderDisabledNextLink(nextAriaLabel, nextLabel)
        : renderEnabledNextLink(nextAriaLabel, nextLabel, nextBtnHandler)}
    </div>
  );
};
